import React, { useState, useEffect } from "react";
import { CloseIcon } from "./Icons";
import offerup from "../services/offerup";
import facebook from "../services/facebook";
import ebay from "../services/ebay";
import { marketplace } from "../services/api";

/**
 * OfferUp Icon component
 */
const OfferUpIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill="#00D498" />
    <path
      d="M12 6C8.686 6 6 8.686 6 12C6 15.314 8.686 18 12 18C15.314 18 18 15.314 18 12C18 8.686 15.314 6 12 6ZM12 15.5C10.067 15.5 8.5 13.933 8.5 12C8.5 10.067 10.067 8.5 12 8.5C13.933 8.5 15.5 10.067 15.5 12C15.5 13.933 13.933 15.5 12 15.5Z"
      fill="white"
    />
  </svg>
);

/**
 * Facebook Marketplace Icon component
 */
const FacebookIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill="#1877F2" />
    <path
      d="M16.5 8H14.5C13.395 8 12.5 8.895 12.5 10V12H16L15.5 16H12.5V22H8.5V16H6V12H8.5V10C8.5 6.686 11.186 4 14.5 4H16.5V8Z"
      fill="white"
    />
  </svg>
);

/**
 * eBay Icon component
 */
const EbayIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill="#F5F5F5" />
    <text
      x="12"
      y="15.5"
      textAnchor="middle"
      fontFamily="Arial, Helvetica, sans-serif"
      fontSize="8"
      fontWeight="bold"
      fill="#E53238"
    >
      e<tspan fill="#0064D2">b</tspan>
      <tspan fill="#F5AF02">a</tspan>
      <tspan fill="#86B817">y</tspan>
    </text>
  </svg>
);

/**
 * Modal for selecting which marketplace(s) to list on
 */
function MarketplaceSelectorModal({
  product,
  imageUrl,
  onClose,
  onSelectOfferUp,
  onSelectFacebook,
  onSelectEbay,
  existingOfferUpListing,
  existingFacebookListing,
  existingEbayListing,
}) {
  const [offerupConfigured, setOfferupConfigured] = useState(false);
  const [facebookConfigured, setFacebookConfigured] = useState(false);
  const [ebayConfigured, setEbayConfigured] = useState(false);
  const [listings, setListings] = useState({
    offerup: existingOfferUpListing,
    facebook: existingFacebookListing,
    ebay: existingEbayListing,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkConfig = async () => {
      setOfferupConfigured(offerup.isConfigured());
      setFacebookConfigured(facebook.isConfigured());
      setEbayConfigured(ebay.isReadyToList());

      // Check for existing listings
      if (product?.id) {
        try {
          const [offerupRes, facebookRes, ebayRes] = await Promise.all([
            marketplace
              .getByEntityAndMarketplace(product.id, "offerup")
              .catch(() => null),
            marketplace
              .getByEntityAndMarketplace(product.id, "facebook")
              .catch(() => null),
            marketplace
              .getByEntityAndMarketplace(product.id, "ebay")
              .catch(() => null),
          ]);

          setListings({
            offerup: offerupRes?.data?.data || existingOfferUpListing,
            facebook: facebookRes?.data?.data || existingFacebookListing,
            ebay: ebayRes?.data?.data || existingEbayListing,
          });
        } catch (err) {
          console.error("Failed to check listings:", err);
        }
      }

      setLoading(false);
    };

    checkConfig();
  }, [
    product?.id,
    existingOfferUpListing,
    existingFacebookListing,
    existingEbayListing,
  ]);

  const marketplaces = [
    {
      id: "offerup",
      name: "OfferUp",
      icon: OfferUpIcon,
      color: "#00D498",
      bgColor: "rgba(0, 212, 152, 0.1)",
      configured: offerupConfigured,
      listing: listings.offerup,
      onSelect: onSelectOfferUp,
      description: "List locally with fast pickup",
    },
    {
      id: "facebook",
      name: "Facebook Marketplace",
      icon: FacebookIcon,
      color: "#1877F2",
      bgColor: "rgba(24, 119, 242, 0.1)",
      configured: facebookConfigured,
      listing: listings.facebook,
      onSelect: onSelectFacebook,
      description: "Reach your local community",
    },
    {
      id: "ebay",
      name: "eBay",
      icon: EbayIcon,
      color: "#0064D2",
      bgColor: "rgba(0, 100, 210, 0.1)",
      configured: ebayConfigured,
      listing: listings.ebay,
      onSelect: onSelectEbay,
      description: "Sell nationwide with shipping",
    },
  ];

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "450px" }}
        >
          <div
            className="modal-body"
            style={{ textAlign: "center", padding: "2rem" }}
          >
            <div className="spinner" style={{ margin: "0 auto" }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "450px" }}
      >
        <div className="modal-header">
          <h2>List on Marketplace</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon">
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="modal-body">
          <p
            style={{
              marginBottom: "var(--spacing-lg)",
              color: "var(--text-secondary)",
            }}
          >
            Choose where to list "{product?.name}"
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-md)",
            }}
          >
            {marketplaces
              .filter((mp) => mp.onSelect)
              .map((mp) => {
                const Icon = mp.icon;
                const hasListing = !!mp.listing;

                return (
                  <button
                    key={mp.id}
                    onClick={mp.onSelect}
                    className="card"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-md)",
                      padding: "var(--spacing-md)",
                      cursor: "pointer",
                      border: hasListing
                        ? `2px solid ${mp.color}`
                        : "1px solid var(--border-color)",
                      background: hasListing ? mp.bgColor : "var(--bg-primary)",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "var(--radius-md)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: mp.bgColor,
                      }}
                    >
                      <Icon size={28} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "1rem" }}>
                        {mp.name}
                      </div>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {mp.description}
                      </div>
                      {hasListing && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: mp.color,
                            marginTop: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Listed at ${mp.listing.price}
                          {mp.listing.external_url && (
                            // The whole card is a <button>, so a nested <a>
                            // wouldn't navigate reliably — open by hand.
                            <span
                              role="link"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  mp.listing.external_url,
                                  "_blank",
                                  "noopener",
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.stopPropagation();
                                  window.open(
                                    mp.listing.external_url,
                                    "_blank",
                                    "noopener",
                                  );
                                }
                              }}
                              style={{
                                marginLeft: "8px",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              View listing ↗
                            </span>
                          )}
                        </div>
                      )}
                      {!mp.configured && !hasListing && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--warning)",
                            marginTop: "4px",
                          }}
                        >
                          Setup required
                        </div>
                      )}
                    </div>
                    <div style={{ color: mp.color, fontWeight: 500 }}>
                      {hasListing ? "Edit" : "List"} →
                    </div>
                  </button>
                );
              })}
          </div>

          {/* Quick info */}
          <div
            style={{
              marginTop: "var(--spacing-lg)",
              padding: "var(--spacing-md)",
              background: "var(--bg-secondary)",
              borderRadius: "var(--radius-md)",
              fontSize: "0.875rem",
              color: "var(--text-muted)",
            }}
          >
            <strong style={{ color: "var(--text-primary)" }}>
              Product Details:
            </strong>
            <div style={{ marginTop: "var(--spacing-xs)" }}>
              {product?.list_sale_price && (
                <div>
                  Price: ${parseFloat(product.list_sale_price).toFixed(2)}
                </div>
              )}
              {product?.condition && (
                <div>Condition: {product.condition.replace("_", " ")}</div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default MarketplaceSelectorModal;
